import { useQuery } from "@tanstack/react-query";
import getApiServices from "../../services/getApiService";
import {
  getCustomerDetail,
  getDashBoardDetails,
  getOrPostSensors,
} from "../../constants/api-end-pionts";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface SensorData {
  id?: string;
  name: string;
  description?: string;
  lastUpdatedAt?: string | null;
  createdAt?: string | null;
  v?: number | null;
  i?: number | null;
  r?: number | string | null;
  g?: number | null;
  criticalThreshold?: number | null;
}

interface PopupData {
  header: string;
  sensors?: SensorData[];
  footer?: string;
}

const useHome = () => {
  const userId = localStorage.getItem("userId");
  const url = `${getCustomerDetail}/${userId}`;

  const getUserdetails = () => {
    return useQuery({
      queryKey: ["user_data"],
      queryFn: () => getApiServices(url),
    });
  };

  const getDashBoardData = () => {
    return useQuery({
      queryKey: ["dashBoard_data"],
      queryFn: () => getApiServices(`${getDashBoardDetails}`),
    });
  };

  const getAllSensorsData = () => {
    return useQuery({
      queryKey: ["all_sensors"],
      queryFn: () => getApiServices(getOrPostSensors),
    });
  };

  const { data: dashBoardData } = getDashBoardData();
  const { data } = getUserdetails();
  const { data: allSensorsData } = getAllSensorsData();

  // Build lookup map from site's criticalResistanceValue
  const thresholdMap = new Map<string, number>();
  data?.sites?.forEach((site: any) => {
    const threshold =
      site?.criticalResistanceValue != null && !isNaN(Number(site.criticalResistanceValue))
        ? Number(site.criticalResistanceValue)
        : 3.0;
    site?.systems?.forEach((sys: any) => {
      sys?.sensors?.forEach((s: any) => {
        if (s?.id) thresholdMap.set(s.id, threshold);
        if (s?.name) thresholdMap.set(s.name, threshold);
      });
    });
  });

  const normalizeSensor = async (sensor: any): Promise<SensorData> => {
    // Backend Sensor has data: List<SensorData> — get the latest entry
    let latestData = Array.isArray(sensor.data) && sensor.data.length > 0
      ? sensor.data[sensor.data.length - 1]
      : null;

    if (!latestData && sensor.name) {
      try {
        const res = await getApiServices(`/be-service/sensors/latestData?deviceId=${sensor.name}`);
        if (res) latestData = res;
      } catch (err) {
        // Ignored, sensor might not have data yet
      }
    }

    const vVal = latestData?.V ?? latestData?.v ?? latestData?.voltage ?? null;
    const iVal = latestData?.I ?? latestData?.i ?? latestData?.current ?? null;
    const rVal = latestData?.R ?? latestData?.r ?? latestData?.resistance ?? null;
    const gVal = latestData?.G ?? latestData?.g ?? latestData?.ground ?? null;

    const criticalThreshold =
      sensor.criticalThreshold ??
      sensor.criticalResistanceValue ??
      latestData?.criticalResistanceValue ??
      thresholdMap.get(sensor.name) ??
      thresholdMap.get(sensor.id) ??
      3.0;

    return {
      id: sensor.id,
      name: sensor.name,
      description: sensor.description || "No description available.",
      lastUpdatedAt: latestData?.timestamp || sensor.lastUpdatedAt || null,
      createdAt: sensor.createdAt || latestData?.timestamp || null,
      v: vVal,
      i: iVal,
      r: rVal,
      g: gVal,
      criticalThreshold,
    };
  };

  const criticalSensors = (dashBoardData?.criticalSensors || []).map((s: any) => ({ name: s.name, id: s.id }));
  const activeSensors = (dashBoardData?.activeSensors || []).map((s: any) => ({ name: s.name, id: s.id }));
  const inactiveSensors = (dashBoardData?.inactiveSensors || []).map((s: any) => ({ name: s.name, id: s.id }));

  const getUniqueSensorNames = (sensors: SensorData[]): string[] =>
    Array.from(new Set(sensors.map((sensor) => sensor.name)));

  const uniqueCriticalSensors = getUniqueSensorNames(criticalSensors);
  const uniqueActiveSensors = getUniqueSensorNames(activeSensors);
  const uniqueInactiveSensors = getUniqueSensorNames(inactiveSensors);

  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState<PopupData | null>(null);

  const navigate = useNavigate();

  const [sensorsData, setSensorsData] = useState<SensorData[]>([]);

  // Fetch initial readings for all dashboard sensors (from dashboard API, allSensors API, and user details)
  useEffect(() => {
    const fetchLatest = async () => {
      const fromAllSensors = Array.isArray(allSensorsData?.content) ? allSensorsData.content : [];
      const fromUserData = data?.sites?.flatMap((site: any) => site?.systems?.flatMap((sys: any) => sys?.sensors || [])) || [];
      const fromDashBoard = dashBoardData
        ? [
            ...(Array.isArray(dashBoardData.criticalSensors) ? dashBoardData.criticalSensors : []),
            ...(Array.isArray(dashBoardData.activeSensors) ? dashBoardData.activeSensors : []),
            ...(Array.isArray(dashBoardData.inactiveSensors) ? dashBoardData.inactiveSensors : []),
            ...(Array.isArray(dashBoardData.sensors) ? dashBoardData.sensors : []),
          ]
        : [];

      const rawSensors = [...fromAllSensors, ...fromUserData, ...fromDashBoard].filter((s: any) => s && s.name);

      if (rawSensors.length === 0) return;

      // Deduplicate by name
      const uniqueMap = new Map();
      rawSensors.forEach((s: any) => {
        if (!uniqueMap.has(s.name)) {
          uniqueMap.set(s.name, s);
        }
      });
      const uniqueList = Array.from(uniqueMap.values());

      // Instantly set basic sensor list so UI populates immediately
      const initialSensors: SensorData[] = uniqueList.map((s: any) => ({
        id: s.id,
        name: s.name,
        description: s.description || "No description available.",
        lastUpdatedAt: s.lastUpdatedAt || null,
        v: s.v ?? s.V ?? null,
        i: s.i ?? s.I ?? null,
        r: s.r ?? s.R ?? null,
        g: s.g ?? s.G ?? null,
        criticalThreshold:
          s.criticalThreshold ??
          s.criticalResistanceValue ??
          thresholdMap.get(s.name) ??
          thresholdMap.get(s.id) ??
          3.0,
      }));

      setSensorsData(initialSensors);

      // Asynchronously enrich with latest readings
      try {
        const enriched = await Promise.all(uniqueList.map((s: any) => normalizeSensor(s)));
        setSensorsData(enriched);
      } catch (err) {
        console.error("Failed to normalize sensors:", err);
      }
    };

    fetchLatest();
  }, [dashBoardData, allSensorsData, data]);

  return {
    data,
    dashBoardData,
    uniqueCriticalSensors,
    uniqueActiveSensors,
    uniqueInactiveSensors,
    showPopup,
    setShowPopup,
    popupData,
    setPopupData,
    criticalSensors,
    activeSensors,
    inactiveSensors,
    navigate,
    sensorsData,
  };
};

export default useHome;
